import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import Navbar from './Navbar';
import AuthContext from '../context/AuthContext';
import { API_BASE_URL } from '../config/adminConfig';
import { formatHours, humanize, STAGE_LABELS } from './productAnalyticsFormatters';

function ProductAnalytics() {
  const { token, logout } = useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');

  useEffect(() => {
    const controller = new AbortController();

    const loadAnalytics = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fetch(`${API_BASE_URL}/api/admin/product-analytics`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (response.status === 401) {
          logout();
          return;
        }
        if (!response.ok) {
          throw new Error(`Analytics request failed (${response.status})`);
        }
        setAnalytics(await response.json());
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError('Growth analytics are temporarily unavailable. Please retry shortly.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    if (token) loadAnalytics();
    return () => controller.abort();
  }, [logout, token]);

  if (loading) {
    return (
      <Box minH="100vh" bg={pageBg}>
        <Navbar />
        <Box display="flex" justifyContent="center" py={20}>
          <Spinner size="xl" />
        </Box>
      </Box>
    );
  }

  const northStar = analytics?.northStar || {};
  const quality = analytics?.dataQuality || {};
  const latestCohort = analytics?.cohorts?.[0] || {};
  const funnel = analytics?.funnel || [];
  const portfolioFitFunnel = analytics?.portfolioFitFunnel || [];
  const paywallSources = analytics?.paywallSources || [];
  const checkoutBilling = analytics?.checkoutBilling || [];
  const purchaseAttempts = analytics?.purchaseAttempts30d || {};
  const restoreAttempts = analytics?.restoreAttempts30d || {};
  const cohorts = analytics?.cohorts || [];

  return (
    <Box minH="100vh" bg={pageBg}>
      <Navbar />
      <Box maxW="8xl" mx="auto" p={{ base: 4, md: 6 }}>
        <Heading size="xl" mb={2}>Growth &amp; Store Verification</Heading>
        <Text color="gray.600" mb={8}>
          Ordered activation, paywall and checkout funnel. A user only advances when
          each stage happens after the previous one. Historical events without trusted verification cannot establish payment; collected revenue is unavailable without settlement records.
        </Text>

        {error ? (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 6 }} spacing={4} mb={8}>
          <MetricCard
            label="Protected decisions"
            value={northStar.protectedDecisions30d}
            help="Last 30 days"
            tone="blue"
          />
          <MetricCard
            label="Active users"
            value={northStar.activeUsers30d}
            help="Last 30 days"
            tone="green"
          />
          <MetricCard
            label="Verified checkout users"
            value={northStar.paidUsers30d}
            help={`${northStar.checkouts30d || 0} checkouts in 30 days`}
            tone="purple"
          />
          <MetricCard
            label="Portfolio fit reveals"
            value={northStar.portfolioFitReveals30d}
            help="Last 30 days"
            tone="teal"
          />
          <MetricCard
            label="Activation in 48h"
            value={latestCohort.activation48hRate == null ? '—' : `${latestCohort.activation48hRate}%`}
            help="Latest signup cohort"
            tone="orange"
          />
          <MetricCard
            label="Event coverage"
            value={`${quality.eventCoverageRate || 0}%`}
            help={`${quality.usersWithAnyEvents || 0} of ${quality.eligibleUsers || 0} users`}
          />
        </SimpleGrid>

        <Card bg={cardBg} shadow="sm" mb={6}>
          <CardHeader pb={2}>
            <Heading size="md">Native checkout health</Heading>
            <Text color="gray.600" fontSize="sm" mt={1}>
              Store attempts linked end to end by an opaque identifier. Cancellations
              are separated from technical failures; no receipt or purchase token is
              collected in analytics.
            </Text>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4, xl: 6 }} spacing={4} mb={6}>
              <MetricCard
                label="Checkout starts"
                value={purchaseAttempts.starts}
                help="Attributed, last 30 days"
                tone="blue"
              />
              <MetricCard
                label="Completed"
                value={purchaseAttempts.completed}
                help={`${purchaseAttempts.completionRate || 0}% of starts`}
                tone="green"
              />
              <MetricCard
                label="Cancelled"
                value={purchaseAttempts.cancelled}
                help={`${purchaseAttempts.cancellationRate || 0}% of starts`}
                tone="orange"
              />
              <MetricCard
                label="Technical failures"
                value={purchaseAttempts.failed}
                help={`${purchaseAttempts.failureRate || 0}% of starts`}
              />
              <MetricCard
                label="Unresolved"
                value={purchaseAttempts.unresolved}
                help="No terminal event yet"
              />
              <MetricCard
                label="Restore success"
                value={`${restoreAttempts.completionRate || 0}%`}
                help={`${restoreAttempts.completed || 0} of ${restoreAttempts.starts || 0} attempts`}
                tone="teal"
              />
            </SimpleGrid>

            {(purchaseAttempts.attributionCoverageRate || 0) < 90 &&
            (purchaseAttempts.totalStartEvents || 0) > 0 ? (
              <Alert status="warning" mb={6} borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  Attempt attribution covers {purchaseAttempts.attributionCoverageRate || 0}%
                  {' '}of checkout starts. Older app versions do not emit the opaque attempt ID.
                </AlertDescription>
              </Alert>
            ) : null}

            <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6}>
              <AttemptBreakdownTable
                title="By billing"
                dimension="billing"
                rows={purchaseAttempts.byBilling || []}
              />
              <AttemptBreakdownTable
                title="By paywall source"
                dimension="source"
                rows={purchaseAttempts.bySource || []}
              />
              <AttemptBreakdownTable
                title="By variant"
                dimension="variant"
                rows={purchaseAttempts.byVariant || []}
              />
            </SimpleGrid>

            {(purchaseAttempts.failureReasons || []).length ? (
              <Box mt={6}>
                <Text fontWeight="600" mb={2}>Technical failure reasons</Text>
                {(purchaseAttempts.failureReasons || []).map(row => (
                  <Badge key={row.reason} colorScheme="red" mr={2} mb={2}>
                    {humanize(row.reason)}: {row.failures}
                  </Badge>
                ))}
              </Box>
            ) : null}
            {(restoreAttempts.failureReasons || []).length ? (
              <Box mt={4}>
                <Text fontWeight="600" mb={2}>Restore outcomes needing attention</Text>
                {(restoreAttempts.failureReasons || []).map(row => (
                  <Badge key={row.reason} colorScheme="orange" mr={2} mb={2}>
                    {humanize(row.reason)}: {row.failures}
                  </Badge>
                ))}
              </Box>
            ) : null}
          </CardBody>
        </Card>

        {(quality.signupEventCoverageRate || 0) < 80 && quality.eligibleUsers > 0 ? (
          <Alert status="warning" mb={6} borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              Signup event coverage is {quality.signupEventCoverageRate || 0}%.
              {' '}{quality.legacySignupFallbackUsers || 0} historical users use account
              creation time as a fallback, so recent cohorts are more reliable than old ones.
            </AlertDescription>
          </Alert>
        ) : null}

        <Card bg={cardBg} shadow="sm" mb={6}>
          <CardHeader pb={2}>
            <Heading size="md">Ordered monetization funnel</Heading>
            <Text color="gray.600" fontSize="sm" mt={1}>
              Raw users show event coverage; ordered users preserve the intended journey.
            </Text>
          </CardHeader>
          <CardBody>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Stage</Th>
                    <Th isNumeric>Ordered users</Th>
                    <Th isNumeric>Raw users</Th>
                    <Th isNumeric>Drop-off</Th>
                    <Th isNumeric>From previous</Th>
                    <Th isNumeric>From signup</Th>
                    <Th isNumeric>Median from previous</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {funnel.map((stage, index) => (
                    <Tr key={stage.eventName}>
                      <Td>
                        <Text fontWeight="600">
                          {STAGE_LABELS[stage.eventName] || humanize(stage.eventName)}
                        </Text>
                        <Badge colorScheme={index === funnel.length - 1 ? 'purple' : 'blue'} mt={1}>
                          Step {index + 1}
                        </Badge>
                      </Td>
                      <Td isNumeric fontWeight="700">{stage.users}</Td>
                      <Td isNumeric>{stage.rawUniqueUsers}</Td>
                      <Td isNumeric>{stage.droppedFromPrevious}</Td>
                      <Td isNumeric>{stage.conversionFromPrevious}%</Td>
                      <Td isNumeric>{stage.conversionFromSignup}%</Td>
                      <Td isNumeric>{formatHours(stage.medianHoursFromPrevious)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="sm" mb={6}>
          <CardHeader pb={2}>
            <Heading size="md">Portfolio-fit activation funnel</Heading>
            <Text color="gray.600" fontSize="sm" mt={1}>
              Measures the core conversion journey without collecting holdings,
              balances or asset amounts in analytics.
            </Text>
          </CardHeader>
          <CardBody>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Stage</Th>
                    <Th isNumeric>Ordered users</Th>
                    <Th isNumeric>Raw users</Th>
                    <Th isNumeric>Drop-off</Th>
                    <Th isNumeric>From previous</Th>
                    <Th isNumeric>From profile</Th>
                    <Th isNumeric>Median from profile</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {portfolioFitFunnel.map((stage, index) => (
                    <Tr key={stage.eventName}>
                      <Td>
                        <Text fontWeight="600">
                          {STAGE_LABELS[stage.eventName] || humanize(stage.eventName)}
                        </Text>
                        <Badge
                          colorScheme={index === portfolioFitFunnel.length - 1 ? 'purple' : 'teal'}
                          mt={1}
                        >
                          Step {index + 1}
                        </Badge>
                      </Td>
                      <Td isNumeric fontWeight="700">{stage.users}</Td>
                      <Td isNumeric>{stage.rawUniqueUsers}</Td>
                      <Td isNumeric>{stage.droppedFromPrevious}</Td>
                      <Td isNumeric>{stage.conversionFromPrevious}%</Td>
                      <Td isNumeric>{stage.conversionFromFirst}%</Td>
                      <Td isNumeric>{formatHours(stage.medianHoursFromFirst)}</Td>
                    </Tr>
                  ))}
                  {!portfolioFitFunnel.length ? <EmptyRow columns={7} /> : null}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={6}>
          <Card bg={cardBg} shadow="sm">
            <CardHeader pb={2}>
              <Heading size="md">Paywall conversion by source</Heading>
              <Text color="gray.600" fontSize="sm" mt={1}>
                Checkout users have a server-verified production store event after viewing that paywall source. This is not a cash-revenue report.
              </Text>
            </CardHeader>
            <CardBody>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Source</Th>
                      <Th isNumeric>Views</Th>
                      <Th isNumeric>Users</Th>
                      <Th isNumeric>Paid</Th>
                      <Th isNumeric>Conversion</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paywallSources.map(row => (
                      <Tr key={row.source}>
                        <Td>{humanize(row.source)}</Td>
                        <Td isNumeric>{row.views}</Td>
                        <Td isNumeric>{row.uniqueUsers}</Td>
                        <Td isNumeric>{row.checkoutUsers}</Td>
                        <Td isNumeric fontWeight="700">{row.conversionRate}%</Td>
                      </Tr>
                    ))}
                    {!paywallSources.length ? <EmptyRow columns={5} /> : null}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>

          <Card bg={cardBg} shadow="sm">
            <CardHeader pb={2}>
              <Heading size="md">Completed checkout mix</Heading>
            </CardHeader>
            <CardBody>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Billing / platform</Th>
                      <Th isNumeric>Checkouts</Th>
                      <Th isNumeric>Unique users</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {checkoutBilling.map(row => (
                      <Tr key={row.billing}>
                        <Td>{humanize(row.billing)}</Td>
                        <Td isNumeric>{row.checkouts}</Td>
                        <Td isNumeric>{row.uniqueUsers}</Td>
                      </Tr>
                    ))}
                    {!checkoutBilling.length ? <EmptyRow columns={3} /> : null}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        </SimpleGrid>

        <Card bg={cardBg} shadow="sm">
          <CardHeader pb={2}>
            <Heading size="md">Weekly acquisition cohorts</Heading>
          </CardHeader>
          <CardBody>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Cohort week</Th>
                    <Th isNumeric>Signups</Th>
                    <Th isNumeric>Activation 48h</Th>
                    <Th isNumeric>Week 1 return</Th>
                    <Th isNumeric>Week 4 return</Th>
                    <Th isNumeric>Paid</Th>
                    <Th isNumeric>Shared</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {cohorts.map(row => (
                    <Tr key={row.cohortWeek}>
                      <Td fontWeight="600">{row.cohortWeek}</Td>
                      <Td isNumeric>{row.signups}</Td>
                      <Td isNumeric>{row.activation48hRate == null ? '—' : `${row.activation48hRate}%`}</Td>
                      <Td isNumeric>{row.w1RetentionRate == null ? '—' : `${row.w1RetentionRate}%`}</Td>
                      <Td isNumeric>{row.w4RetentionRate == null ? '—' : `${row.w4RetentionRate}%`}</Td>
                      <Td isNumeric>{row.paidConversionRate}%</Td>
                      <Td isNumeric>{row.shareRate}%</Td>
                    </Tr>
                  ))}
                  {!cohorts.length ? <EmptyRow columns={7} /> : null}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

        <Card bg={cardBg} mt={6}>
          <CardHeader><Heading size="md">Observed app entry channels</Heading></CardHeader>
          <CardBody>
            <Text mb={3}>Explicit app links only. Store clicks, organic installs and reinstalls cannot be joined reliably and remain unmeasured.</Text>
            {(analytics?.entryAttribution?.channels || []).map(row => <Text key={row.channel}>{row.channel}: {row.events} entries, {row.users} users</Text>)}
            {!analytics?.entryAttribution && <Text>Entry attribution is not available for this dataset.</Text>}
          </CardBody>
        </Card>

        <Text mt={6} color="gray.500" fontSize="sm">
          Updated at: {analytics?.updatedAt
            ? new Date(analytics.updatedAt).toLocaleString()
            : 'n/a'}
        </Text>
      </Box>
    </Box>
  );
}

function MetricCard({ label, value, help, tone = 'gray' }) {
  const colors = {
    gray: 'gray.900',
    blue: 'blue.500',
    green: 'green.500',
    purple: 'purple.500',
    orange: 'orange.500',
    teal: 'teal.500',
  };

  return (
    <Card shadow="sm">
      <CardBody>
        <Stat>
          <StatLabel>{label}</StatLabel>
          <StatNumber color={colors[tone] || colors.gray}>{value ?? 0}</StatNumber>
          <StatHelpText mb={0}>{help}</StatHelpText>
        </Stat>
      </CardBody>
    </Card>
  );
}

function EmptyRow({ columns }) {
  return (
    <Tr>
      <Td colSpan={columns} textAlign="center" color="gray.500" py={6}>
        No events collected yet.
      </Td>
    </Tr>
  );
}

function AttemptBreakdownTable({ title, dimension, rows }) {
  return (
    <Box>
      <Text fontWeight="600" mb={2}>{title}</Text>
      <TableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>{humanize(dimension)}</Th>
              <Th isNumeric>Starts</Th>
              <Th isNumeric>Done</Th>
              <Th isNumeric>Rate</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map(row => (
              <Tr key={row[dimension]}>
                <Td>{humanize(row[dimension])}</Td>
                <Td isNumeric>{row.starts}</Td>
                <Td isNumeric>{row.completed}</Td>
                <Td isNumeric fontWeight="700">{row.completionRate}%</Td>
              </Tr>
            ))}
            {!rows.length ? <EmptyRow columns={4} /> : null}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ProductAnalytics;
